import { DPA } from './dpaContent.ts';
import { PRIVACY } from './privacyContent.ts';
import { TERMS } from './termsContent.ts';

/**
 * The three published legal documents, as data.
 *
 * **The prose is not the point of this file; the shape is.** A legal document
 * is quoted at us clause by clause — a due-diligence questionnaire answers
 * "where do you say how long you keep it?" with a link to a numbered section,
 * not to a document — so every section here has an id that is an anchor target,
 * and `/privacy#retention` is a promise made to whoever pasted it into a
 * response. Ids are therefore part of the contract with people outside this
 * company: renaming one silently breaks a link somebody else is relying on, and
 * the section can be rewritten entirely without touching it.
 *
 * **Structured rather than three slabs of JSX**, for two reasons. The table of
 * contents and the anchors are then derived from the same array that renders
 * the body, so a section cannot appear in one and not the other; and the
 * drafting of the actual wording is a separate job from the building of the
 * page, done by somebody filling in strings rather than editing markup.
 *
 * **The version and the date are fields, not sentences in a paragraph.** A
 * legal page whose text has changed and whose date has not is worse than one
 * with no date at all: it is a statement, in writing, that the current wording
 * has been in force since a day on which it was not. Keeping them here — one
 * module, three documents, beside the sections they describe — means the edit
 * that changes the text is in the same diff as the edit that dates it, and a
 * reviewer can see both. It does not make forgetting impossible. It makes
 * forgetting visible.
 */

export type LegalDocumentId = 'terms' | 'privacy' | 'dpa';

export interface LegalSection {
  /**
   * The anchor. Stable across revisions of the wording, because it is cited
   * from outside: a slug that describes the subject rather than the sentence.
   */
  readonly id: string;
  readonly heading: string;
  /** Body copy, one string per paragraph. Plain text — there is no markup layer. */
  readonly paragraphs: readonly string[];
  /**
   * An optional list, rendered after the paragraphs. Legal drafting reaches for
   * one wherever a clause enumerates — the categories of personal data, the
   * grounds for termination — and prose that has to carry an enumeration inside
   * a sentence is prose nobody can check an item against.
   */
  readonly bullets?: readonly string[];
}

export interface LegalDocument {
  readonly id: LegalDocumentId;
  /** Its own address. Imported from `route.ts` so the two cannot disagree. */
  readonly path: string;
  readonly title: string;
  /** One line saying what the document is for, read before anybody reads clause 1. */
  readonly summary: string;
  /**
   * The published revision, cited in a questionnaire alongside the date. Dotted
   * numerals with an optional `-draft` suffix, which is the one thing about it
   * a test can check: what a bump means is a judgement about the wording.
   */
  readonly version: string;
  /** ISO 8601, formatted for display at render time rather than stored twice. */
  readonly updated: string;
  /**
   * Whether the wording below is placeholder text awaiting drafting. Rendered
   * as a notice on the page itself: a legal document that looks finished and is
   * not is the one thing worse than an obviously unfinished one, because a
   * reader has no way to tell which they are looking at.
   */
  readonly draft: boolean;
  readonly sections: readonly LegalSection[];
}

/** Shown at the head of any document still carrying placeholder wording. */
export const DRAFT_NOTICE =
  'This document is a working draft. The wording below is placeholder text describing what ' +
  'each section will contain, and it is not yet a statement of our terms. It is published ' +
  'here so that the structure, the addresses and the section anchors are settled before the ' +
  'wording is.';

/** Written where the drafted prose goes, so an unfinished section reads as one. */
/*
 * The prose lives beside this file, one module per document, and this one holds
 * only the shape and the registry.
 *
 * Splitting them is not tidiness. A legal document is edited by somebody
 * reading it as a document — a clause at a time, with a solicitor's comments
 * beside them — and the thing they must not have to think about while doing it
 * is a registry, a route constant or a date formatter. It also keeps this file
 * inside the size band the repository holds itself to: with three documents'
 * real text inline it was already past four hundred lines with placeholders in
 * place of every paragraph.
 */
const BY_ID: Record<LegalDocumentId, LegalDocument> = { terms: TERMS, privacy: PRIVACY, dpa: DPA };

/**
 * Order matters wherever they are listed together: terms first because it is
 * the agreement, privacy second, and the DPA last because it is meaningless
 * read without the privacy policy beside it.
 */
export const LEGAL_DOCUMENTS: readonly LegalDocument[] = [BY_ID.terms, BY_ID.privacy, BY_ID.dpa];

export const documentFor = (id: LegalDocumentId): LegalDocument => BY_ID[id];

const UPDATED_FORMAT = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  // Fixed, because the stored value is a calendar date rather than an instant.
  // Left to the reader's zone, "27 August" west of Greenwich becomes the 26th.
  timeZone: 'UTC',
});

/**
 * The stored ISO date, as a person reads it.
 *
 * Formatted here rather than written out beside the ISO string, because two
 * spellings of one date are two things to keep in step and only one of them is
 * ever checked. `en-GB` is fixed rather than taken from the browser: this is a
 * date in a document governed by one jurisdiction, and 03/04/2026 means two
 * different days depending on where the reader happens to be.
 */
export const formatUpdated = (iso: string): string =>
  UPDATED_FORMAT.format(new Date(`${iso}T00:00:00Z`));
